import api from "./api";

/**
 * Interface for Customer data
 */
export interface Customer {
  customerId: string;
  customerNumber: string;
  customerName: string;
  primaryContactEmail?: string;
  entraTenantId?: string;
  tenantPrepDate?: string;  // ISO date string of when the tenant was prepped
}

/**
 * Interface for API response containing customers
 */
interface CustomerResponse {
  message: string;
  data: Customer[];
  count: number;
}

/**
 * Interface for API response containing a single customer
 */
interface SingleCustomerResponse {
  message: string;
  data: Customer;
}

/**
 * Get all customers
 * @returns Promise with customer data
 */
export const getCustomers = async (): Promise<Customer[]> => {
  try {
    // Log the request for debugging
    console.log("Fetching customers from API...");
    
    // Make the API request
    const response = await api.get<CustomerResponse>("/customer");
    
    // Log the response for debugging
    console.log("Customer API response:", response);
    
    // Check if the response has the expected format
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    } else if (response.data && Array.isArray(response.data)) {
      // Handle case where the API returns an array directly
      return response.data;
    } else if (response.data && typeof response.data === 'object') {
      // Handle case where the API returns a different format
      console.log("Unexpected API response format, attempting to extract customer data");
      
      // Try to extract customer data from the response
      const extractedData = Object.values(response.data).find(value => 
        Array.isArray(value) && value.length > 0 && value[0].customerId
      );
      
      if (extractedData && Array.isArray(extractedData)) {
        return extractedData as Customer[];
      }
      
      // If we can't extract customer data, return an empty array
      console.error("Could not extract customer data from response:", response.data);
      return [];
    }
    
    // Default to empty array if we can't determine the format
    console.error("Unknown API response format:", response.data);
    return [];
  } catch (error) {
    console.error("Error fetching customers:", error);
    throw error;
  }
};

/**
 * Get a single customer by ID
 * @param id Customer ID
 * @returns Promise with customer data
 */
export const getCustomer = async (id: string): Promise<Customer> => {
  try {
    const response = await api.get<SingleCustomerResponse>(`/customer/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching customer ${id}:`, error);
    throw error;
  }
};
